import { addError, addLoading, getLoading, removeLoadingAndError } from '../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { IAppState } from '../core/redux/IAppState';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../core/redux/api/reduxStandardApiAction';
import { apiTrainingDocumentById } from '../core/redux/api/endpoints';
import { ACTION_TYPE } from '../core/redux/actionType';
import { assoc } from '../language/module';


const KEY_ACTION = ACTION_TYPE.DELETE_TRAINING_DOC_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.DELETE_TRAINING_DOC_REQUEST,
  ACTION_TYPE.DELETE_TRAINING_DOC_RESPONSE,
  ACTION_TYPE.DELETE_TRAINING_DOC_ERROR,
];


/**
 * Creates an action to dispatch that deletes a training document. The API authorizes writes by the caller's role,
 * sent as a query parameter. Success is a 204 with no body, so the reducer removes the document by the id in meta.
 * @param documentId the document to delete
 */
export const deleteTrainingDocumentAction = (documentId: number) => makeApiAction(
  {
    method:   'DELETE',
    endpoint: (state: IAppState) => `${apiTrainingDocumentById(documentId)}?role=${encodeURIComponent(state.userState?.role ?? '')}`,
    headers:  (state: IAppState) => ({
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    types:    ACTIONS.map(action => ({ type: action, meta: { documentId } })) as RsaaTypes,
    bailout:  state => !!getLoading(state, String(documentId), KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<unknown> = (state, action, error, isLoading) => {
  const documentId = action.meta.documentId as number;

  if (isLoading) {
    return addLoading(state, String(documentId), KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, String(documentId), KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, String(documentId), KEY_ACTION);

  return assoc(
    'trainingDocuments',
    (noLoadingOrError.trainingDocuments ?? []).filter(doc => doc.id !== documentId),
    noLoadingOrError,
  );
};


export const deleteTrainingDocumentReducer = makeApiReducer<unknown>(ACTIONS, KEY_ACTION, reducer);
